/**
 * Lab booking: the calendar, the slot picker and the submission, for every
 * [r2o_lab_booking] on the page (templates/lab-booking/form.php).
 *
 * The calendar is built here rather than server side because the page is
 * served from a page cache: a month rendered in PHP would still say
 * "September" in October. Availability is fetched from the REST route when
 * the page loads for the same reason, since a month of it baked into a cached
 * page would offer slots that had already gone.
 *
 * What the calendar shows only helps the visitor choose. The booking route
 * checks the slot again inside the transaction that writes the row, so two
 * people reaching for the last slot are told apart correctly however stale
 * either browser's picture was.
 *
 * A booking the route accepts is sent to GA4 as lab_booking, with its module
 * and the access the route made it under.
 *
 * Dates are "YYYY-MM-DD" strings, formatted from UTC: the strings compare
 * correctly with < and >, and UTC keeps the browser's own timezone from
 * moving a day across midnight.
 */

import { track } from './analytics/track.js';

/*
 * The states the availability route reports (the Day_State enum), and what
 * each means here: the modifier a day carries, whether it can be chosen, and
 * the wording that says so to a screen reader.
 */
const DAY_STATES = {
	past: { modifier: 'past', bookable: false, announce: null },
	available: {
		modifier: 'available',
		bookable: true,
		announce: 'dayAvailable',
	},
	partially_booked: {
		modifier: 'partial',
		bookable: true,
		announce: 'dayPartial',
	},
	fully_booked: { modifier: 'full', bookable: false, announce: 'dayFull' },
};

// What a key does to the focused day, in days.
const DAY_STEPS = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

const locale = document.documentElement.lang || 'en';

const formats = {
	month: new Intl.DateTimeFormat( locale, {
		month: 'long',
		year: 'numeric',
		timeZone: 'UTC',
	} ),
	weekday: new Intl.DateTimeFormat( locale, {
		weekday: 'short',
		timeZone: 'UTC',
	} ),
	weekdayLong: new Intl.DateTimeFormat( locale, {
		weekday: 'long',
		timeZone: 'UTC',
	} ),
	day: new Intl.DateTimeFormat( locale, {
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		timeZone: 'UTC',
	} ),
};

const pad = ( number ) => String( number ).padStart( 2, '0' );

const asUtcDate = ( iso ) => {
	const [ year, month, day ] = iso.split( '-' ).map( Number );

	return new Date( Date.UTC( year, month - 1, day ) );
};

const isoOf = ( date ) => date.toISOString().slice( 0, 10 );

const monthOf = ( iso ) => iso.slice( 0, 7 );

const daysIn = ( month ) => {
	const [ year, number ] = month.split( '-' ).map( Number );

	return new Date( Date.UTC( year, number, 0 ) ).getUTCDate();
};

const addDays = ( iso, days ) => {
	const date = asUtcDate( iso );

	date.setUTCDate( date.getUTCDate() + days );

	return isoOf( date );
};

// The same day of another month, or that month's last day if it is shorter.
const addMonths = ( iso, months ) => {
	const date = asUtcDate( iso );
	const first = new Date(
		Date.UTC( date.getUTCFullYear(), date.getUTCMonth() + months, 1 )
	);
	const month = isoOf( first ).slice( 0, 7 );

	return `${ month }-${ pad( Math.min( date.getUTCDate(), daysIn( month ) ) ) }`;
};

// Weeks start on Monday, as they do in Europe.
const weekday = ( iso ) => ( asUtcDate( iso ).getUTCDay() + 6 ) % 7;

/**
 * Today where the labs are, which need not be today where the visitor is. A
 * browser without timezone data falls back to its own date; the
 * availability route answers with the server's day and replaces this.
 *
 * @param {string} timeZone The labs' timezone.
 */
const todayIn = ( timeZone ) => {
	try {
		return new Intl.DateTimeFormat( 'en-CA', {
			timeZone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		} ).format( new Date() );
	} catch {
		const now = new Date();

		return `${ now.getFullYear() }-${ pad( now.getMonth() + 1 ) }-${ pad(
			now.getDate()
		) }`;
	}
};

const element = ( tag, className, text ) => {
	const node = document.createElement( tag );

	if ( className ) {
		node.className = className;
	}

	if ( undefined !== text ) {
		node.textContent = text;
	}

	return node;
};

/**
 * One calendar and its form.
 *
 * @param {HTMLElement} container The shortcode's root element.
 */
function init( container ) {
	const form = container.querySelector( '[data-r2o-form]' );
	const calendarRoot = container.querySelector( '[data-r2o-calendar]' );

	if ( ! form || ! calendarRoot ) {
		return;
	}

	const config = JSON.parse( container.dataset.r2oConfig || '{}' );
	const strings = config.i18n || {};
	const text = ( key, value ) =>
		( strings[ key ] || '' ).replace( '%s', value ?? '' );

	const moduleField = form.elements.namedItem( 'lab_module' );
	const picker = container.querySelector( '[data-r2o-module]' );
	const dateInput = form.querySelector( '[data-r2o-slot-date]' );
	const slotSet = form.querySelector( '[data-r2o-slots]' );
	const slotInputs = [
		...form.querySelectorAll( 'input[name="time_slot"]' ),
	];
	const selection = form.querySelector( '[data-r2o-selection]' );
	const message = form.querySelector( '[data-r2o-message]' );
	const submit = form.querySelector( '[data-r2o-submit]' );

	const today = todayIn( config.timeZone || 'Europe/Amsterdam' );

	const state = {
		module: moduleField ? moduleField.value : '',
		today,
		view: monthOf( today ),
		// The one day in the grid that Tab reaches; the arrow keys move it.
		focusDate: today,
		selected: null,
		// Availability by "module|month", the requests still fetching it,
		// and the months that could not be read.
		months: new Map(),
		pending: new Map(),
		failed: new Set(),
		submitting: false,
	};

	// The template's prompt; kept so the wording has one source.
	const initialPrompt = selection ? selection.textContent.trim() : '';

	const calendar = buildCalendar();

	const key = ( month ) => `${ state.module }|${ month }`;

	/*
	 * Availability.
	 */

	function dayInfo( iso ) {
		return state.months.get( key( monthOf( iso ) ) )?.[ iso ] ?? null;
	}

	/**
	 * How a day reads: what the server said, or a guess while it is asked.
	 *
	 * The guess is generous and offers every day still to come. Closing days
	 * on no information would hide slots that are free, and the route checks
	 * the slot again anyway, so the worst a generous calendar does is let
	 * somebody pick a slot they are then told has gone.
	 *
	 * @param {string} iso The day.
	 */
	function dayState( iso ) {
		const info = dayInfo( iso );

		if ( info && DAY_STATES[ info.state ] ) {
			return info.state;
		}

		return iso < state.today ? 'past' : 'available';
	}

	/**
	 * Fetches a month's availability for the chosen module, once.
	 *
	 * An answer already held is reused and a request in flight is joined, so
	 * stepping back and forth does not ask again. A month that could not be
	 * read is not kept, so the next look at it tries again.
	 *
	 * @param {string} month "YYYY-MM".
	 */
	function loadMonth( month ) {
		const id = key( month );

		if ( ! state.module || state.months.has( id ) ) {
			return Promise.resolve();
		}

		if ( state.pending.has( id ) ) {
			return state.pending.get( id );
		}

		const url = new URL( config.availabilityUrl, window.location.href );

		url.searchParams.set( 'module', state.module );
		url.searchParams.set( 'month', month );

		const request = fetch( url, {
			credentials: 'same-origin',
			cache: 'no-store',
			headers: { Accept: 'application/json' },
		} )
			.then( ( response ) => ( response.ok ? response.json() : null ) )
			.catch( () => null )
			.then( ( body ) => {
				state.pending.delete( id );

				if ( ! body?.days ) {
					state.failed.add( id );

					return;
				}

				state.failed.delete( id );
				state.months.set( id, body.days );

				// The server knows the lab's day; the browser only guessed.
				if ( body.today ) {
					state.today = body.today;
				}
			} );

		state.pending.set( id, request );

		return request;
	}

	/**
	 * Shows a month straight away and fills it in when its answer lands, if
	 * the visitor is still looking at it.
	 *
	 * @param {string} month "YYYY-MM".
	 */
	function showMonth( month ) {
		state.view = month;
		renderMonth();

		const id = key( month );

		loadMonth( month ).then( () => {
			if ( id === key( state.view ) ) {
				renderMonth();
				reconcile();
			}
		} );
	}

	/**
	 * Drops what is held for a month and asks again: after the route said a
	 * slot has gone, what is on screen is known to be out of date.
	 *
	 * @param {string} month "YYYY-MM".
	 */
	function refreshMonth( month ) {
		state.months.delete( key( month ) );

		return loadMonth( month ).then( () => {
			renderMonth();
			reconcile();
		} );
	}

	/**
	 * Lets go of a chosen day that can no longer be booked, and shuts the
	 * slots that have gone on the day still chosen.
	 */
	function reconcile() {
		if (
			state.selected &&
			! DAY_STATES[ dayState( state.selected ) ].bookable
		) {
			deselect();
		}

		renderSlots();
	}

	/*
	 * The calendar.
	 */

	/**
	 * The parts that stay: the month title, the arrows and the weekday row.
	 * Only the days are drawn again, so focus on an arrow survives a change
	 * of month.
	 */
	function buildCalendar() {
		const header = element( 'div', 'r2o-lab-booking-calendar-header' );
		const title = element( 'p', 'r2o-lab-booking-month' );
		const previous = navButton( 'previous', text( 'previousMonth' ) );
		const next = navButton( 'next', text( 'nextMonth' ) );

		title.id = `${ container.id }-month`;
		title.setAttribute( 'aria-live', 'polite' );

		previous.addEventListener( 'click', () => stepMonth( -1 ) );
		next.addEventListener( 'click', () => stepMonth( 1 ) );

		header.append( previous, title, next );

		const table = element( 'table', 'r2o-lab-booking-grid' );
		const head = table.createTHead().insertRow();
		const body = table.createTBody();

		table.setAttribute( 'aria-labelledby', title.id );

		// 1 January 2024 was a Monday.
		for ( let offset = 0; offset < 7; offset++ ) {
			const date = new Date( Date.UTC( 2024, 0, 1 + offset ) );
			const cell = element(
				'th',
				'r2o-lab-booking-weekday',
				formats.weekday.format( date )
			);

			cell.scope = 'col';
			cell.abbr = formats.weekdayLong.format( date );
			head.append( cell );
		}

		body.addEventListener( 'click', ( event ) => {
			const day = event.target.closest( '[data-date]' );

			if ( day && 'true' !== day.getAttribute( 'aria-disabled' ) ) {
				selectDay( day.dataset.date );
			}
		} );

		body.addEventListener( 'keydown', onGridKey );

		const status = element( 'p', 'r2o-lab-booking-status' );

		calendarRoot.replaceChildren( header, table, status );

		return { title, previous, body, status };
	}

	function navButton( direction, label ) {
		const button = element(
			'button',
			`r2o-lab-booking-nav r2o-lab-booking-nav-${ direction }`
		);

		button.type = 'button';
		button.setAttribute( 'aria-label', label );
		button.innerHTML =
			'<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="M10 3 5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

		return button;
	}

	// Stepping back past the lab's current month would only show days gone.
	const canStepBack = () => state.view > monthOf( state.today );

	function stepMonth( delta ) {
		if ( delta < 0 && ! canStepBack() ) {
			return;
		}

		showMonth( addMonths( `${ state.view }-01`, delta ).slice( 0, 7 ) );
	}

	/**
	 * Draws the month in view.
	 *
	 * Focus that was on a day moves to the same day in the new cells, so an
	 * answer arriving while somebody is on the grid does not throw them off.
	 */
	function renderMonth() {
		const month = state.view;
		const hadFocus = calendar.body.contains(
			container.ownerDocument.activeElement
		);
		const known = ! state.module || state.months.has( key( month ) );
		const failed = state.failed.has( key( month ) );

		if ( monthOf( state.focusDate ) !== month ) {
			state.focusDate = focusTarget( month );
		}

		calendar.title.textContent = formats.month.format(
			asUtcDate( `${ month }-01` )
		);
		calendar.previous.setAttribute(
			'aria-disabled',
			canStepBack() ? 'false' : 'true'
		);

		const rows = [];
		let row = document.createElement( 'tr' );

		for ( let blank = 0; blank < weekday( `${ month }-01` ); blank++ ) {
			row.append( document.createElement( 'td' ) );
		}

		for ( let day = 1; day <= daysIn( month ); day++ ) {
			const iso = `${ month }-${ pad( day ) }`;
			const cell = document.createElement( 'td' );

			cell.append( dayButton( iso, day ) );
			row.append( cell );

			if ( 6 === weekday( iso ) ) {
				rows.push( row );
				row = document.createElement( 'tr' );
			}
		}

		if ( row.children.length ) {
			rows.push( row );
		}

		calendar.body.replaceChildren( ...rows );

		const loading = Boolean( state.module ) && ! known && ! failed;

		container.classList.toggle( 'is-loading', loading );
		calendar.body.setAttribute( 'aria-busy', loading ? 'true' : 'false' );

		let status = '';

		if ( ! state.module ) {
			status = text( 'moduleFirst' );
		} else if ( failed ) {
			status = text( 'availabilityFailed' );
		} else if ( loading ) {
			status = text( 'loadingDays' );
		}

		calendar.status.textContent = status;

		if ( hadFocus ) {
			focusDay( state.focusDate );
		}
	}

	/**
	 * The day Tab lands on in a month: the chosen day, else today, else the
	 * first day that can be booked, else the first.
	 *
	 * @param {string} month "YYYY-MM".
	 */
	function focusTarget( month ) {
		if ( state.selected && monthOf( state.selected ) === month ) {
			return state.selected;
		}

		if ( monthOf( state.today ) === month ) {
			return state.today;
		}

		for ( let day = 1; day <= daysIn( month ); day++ ) {
			const iso = `${ month }-${ pad( day ) }`;

			if ( DAY_STATES[ dayState( iso ) ].bookable ) {
				return iso;
			}
		}

		return `${ month }-01`;
	}

	/**
	 * One day. A day that cannot be booked stays focusable and says why,
	 * rather than dropping out of the arrow keys' path.
	 *
	 * @param {string} iso        The day.
	 * @param {number} dayOfMonth Its number.
	 */
	function dayButton( iso, dayOfMonth ) {
		const name = dayState( iso );
		const meaning = DAY_STATES[ name ];
		const button = element(
			'button',
			`r2o-lab-booking-day r2o-lab-booking-day-${ meaning.modifier }`,
			String( dayOfMonth )
		);
		const long = formats.day.format( asUtcDate( iso ) );

		button.type = 'button';
		button.dataset.date = iso;
		button.dataset.state = name;
		button.tabIndex = iso === state.focusDate ? 0 : -1;
		button.setAttribute(
			'aria-label',
			meaning.announce ? text( meaning.announce, long ) : long
		);

		if ( iso === state.today ) {
			button.setAttribute( 'aria-current', 'date' );
		}

		if ( meaning.bookable ) {
			button.setAttribute(
				'aria-pressed',
				iso === state.selected ? 'true' : 'false'
			);
		} else {
			button.setAttribute( 'aria-disabled', 'true' );
		}

		return button;
	}

	function focusDay( iso ) {
		calendar.body.querySelector( `[data-date="${ iso }"]` )?.focus();
	}

	/**
	 * The arrow keys move a day or a week, Home and End to the ends of the
	 * week, Page Up and Page Down a month, crossing into the next month
	 * where they have to.
	 *
	 * @param {KeyboardEvent} event The key.
	 */
	function onGridKey( event ) {
		const from = event.target.dataset?.date;

		if ( ! from || event.altKey || event.ctrlKey || event.metaKey ) {
			return;
		}

		let to;

		if ( event.key in DAY_STEPS ) {
			to = addDays( from, DAY_STEPS[ event.key ] );
		} else if ( 'Home' === event.key ) {
			to = addDays( from, -weekday( from ) );
		} else if ( 'End' === event.key ) {
			to = addDays( from, 6 - weekday( from ) );
		} else if ( 'PageUp' === event.key || 'PageDown' === event.key ) {
			to = addMonths( from, 'PageUp' === event.key ? -1 : 1 );
		} else {
			return;
		}

		event.preventDefault();

		if ( monthOf( to ) < monthOf( state.today ) ) {
			return;
		}

		state.focusDate = to;

		if ( monthOf( to ) !== state.view ) {
			showMonth( monthOf( to ) );
		} else {
			for ( const day of calendar.body.querySelectorAll(
				'[data-date]'
			) ) {
				day.tabIndex = day.dataset.date === to ? 0 : -1;
			}
		}

		focusDay( to );
	}

	/*
	 * The day and the slots.
	 */

	function selectDay( iso ) {
		state.selected = iso;
		state.focusDate = iso;
		dateInput.value = iso;
		slotSet.hidden = false;
		selection.textContent = text(
			'slotsOn',
			formats.day.format( asUtcDate( iso ) )
		);

		clearError( 'slot_date' );
		renderSlots();
		renderMonth();
	}

	function deselect() {
		state.selected = null;
		dateInput.value = '';
		slotSet.hidden = true;
		selection.textContent = initialPrompt;
	}

	/**
	 * Offers the slots on the chosen day, with the ones already booked shut
	 * and the ones that have begun (today only) left out. Which have begun
	 * is the server's answer, not the browser's clock. A slot picked before
	 * the answer said it had gone is unpicked, so the form cannot be sent
	 * for it.
	 */
	function renderSlots() {
		const info = state.selected ? dayInfo( state.selected ) : null;
		const taken = info?.taken || [];
		const started = info?.started || [];

		for ( const input of slotInputs ) {
			const isStarted = started.includes( input.value );
			const isTaken = ! isStarted && taken.includes( input.value );
			const label = input.closest( '.r2o-lab-booking-slot' );
			const note = label?.querySelector( '[data-r2o-slot-note]' );

			input.disabled = isTaken || isStarted;

			if ( input.disabled ) {
				input.checked = false;
			}

			if ( label ) {
				label.hidden = isStarted;
			}

			label?.classList.toggle( 'is-taken', isTaken );

			if ( note ) {
				note.textContent = isTaken ? text( 'slotTaken' ) : '';
				note.hidden = ! isTaken;
			}
		}
	}

	function onModuleChange() {
		state.module = picker.value;
		state.focusDate = focusTarget( state.view );
		clearError( 'lab_module' );
		showMonth( state.view );
		reconcile();
	}

	/*
	 * Messages and errors. The wording comes from the server wherever the
	 * rule lives there.
	 */

	function showMessage( words, kind ) {
		message.textContent = words;
		message.dataset.kind = kind;
	}

	function clearMessage() {
		message.textContent = '';
		delete message.dataset.kind;
	}

	/**
	 * The controls a field name stands for: a radio group is several.
	 *
	 * @param {string} field The field's name.
	 */
	function controls( field ) {
		const named = form.elements.namedItem( field );

		if ( ! named ) {
			return [];
		}

		// An element has a tag name; a RadioNodeList has none.
		return named.tagName ? [ named ] : [ ...named ];
	}

	/**
	 * Marks a field and says what is wrong with it under the field, if it
	 * has a place for that.
	 *
	 * @param {string} field The field's name.
	 * @param {string} words What is wrong; empty to only mark it.
	 * @return {boolean} Whether the field has a place for the words.
	 */
	function showError( field, words ) {
		const slot = container.querySelector( `[data-r2o-error="${ field }"]` );

		for ( const control of controls( field ) ) {
			if ( 'hidden' !== control.type ) {
				control.setAttribute( 'aria-invalid', 'true' );
			}
		}

		if ( ! slot ) {
			return false;
		}

		slot.textContent = words;
		slot.hidden = '' === words;

		return true;
	}

	function clearError( field ) {
		const slot = container.querySelector( `[data-r2o-error="${ field }"]` );

		for ( const control of controls( field ) ) {
			control.removeAttribute( 'aria-invalid' );
		}

		if ( slot ) {
			slot.textContent = '';
			slot.hidden = true;
		}
	}

	function clearErrors() {
		for ( const slot of container.querySelectorAll( '[data-r2o-error]' ) ) {
			clearError( slot.dataset.r2oError );
		}
	}

	/**
	 * What the route refused, shown where it applies: each field's message
	 * under the field, and the route's summary in the status line. A message
	 * the summary already says is not repeated under its field.
	 *
	 * @param {Object} body The route's error.
	 */
	function showRejection( body ) {
		const errors = body?.data?.errors || {};
		const summary = [ body?.message || text( 'bookingFailed' ) ];

		for ( const [ field, words ] of Object.entries( errors ) ) {
			const placed = showError(
				field,
				words === summary[ 0 ] ? '' : words
			);

			if ( ! placed && words !== summary[ 0 ] ) {
				summary.push( words );
			}
		}

		showMessage( summary.join( ' ' ), 'error' );
	}

	/**
	 * The first control in the form's order that is marked wrong and can take
	 * focus.
	 */
	function focusFirstInvalid() {
		const invalid = [
			...container.querySelectorAll( '[aria-invalid="true"]' ),
		].find( ( control ) => ! control.disabled );

		invalid?.focus();
	}

	/*
	 * Sending.
	 */

	/**
	 * The module, the day and the slot are not ordinary fields, so they are
	 * checked here; the browser checks the rest through `required` before
	 * this runs, and the route checks everything again.
	 *
	 * @param {SubmitEvent} event The submission.
	 */
	function onSubmit( event ) {
		event.preventDefault();

		if ( state.submitting ) {
			return;
		}

		clearErrors();
		clearMessage();

		if ( ! state.module ) {
			showError( 'lab_module', text( 'chooseModule' ) );
			picker?.focus();

			return;
		}

		if ( ! state.selected ) {
			showError( 'slot_date', text( 'chooseDay' ) );
			focusDay( state.focusDate );

			return;
		}

		if ( ! slotInputs.some( ( input ) => input.checked ) ) {
			const open = slotInputs.filter( ( input ) => ! input.disabled );

			showError(
				'time_slot',
				open.length ? text( 'chooseSlot' ) : text( 'dayFullyBooked' )
			);
			(
				open[ 0 ] ?? calendar.body.querySelector( '[tabindex="0"]' )
			)?.focus();

			return;
		}

		send();
	}

	/**
	 * Posts the booking.
	 *
	 * The public form sends no nonce: the page it comes from is cached, so a
	 * nonce would be stale for most visitors. Without one WordPress treats
	 * the request as anonymous, which is what makes a logged-in booking
	 * behave exactly like a logged-out one. The enrolled form, shown only to
	 * a logged-in student on a page no cache keeps, carries one, so the
	 * booking is made as the student.
	 */
	function send() {
		const payload = new URLSearchParams( new FormData( form ) );
		const headers = { Accept: 'application/json' };

		if ( config.restNonce ) {
			headers[ 'X-WP-Nonce' ] = config.restNonce;
		}

		// Which page the form was on. Only the browser knows: a referer may
		// never arrive.
		payload.append( 'source_url', window.location.href );

		state.submitting = true;
		submit.setAttribute( 'aria-disabled', 'true' );
		form.setAttribute( 'aria-busy', 'true' );
		showMessage( text( 'booking' ), 'progress' );

		fetch( config.bookingsUrl, {
			method: 'POST',
			credentials: 'same-origin',
			headers,
			body: payload,
		} )
			.then( ( response ) =>
				response
					.json()
					.then( ( body ) => ( { ok: response.ok, body } ) )
			)
			.then( ( { ok, body } ) => {
				settle();

				if ( ok ) {
					track( 'lab_booking', {
						module: state.module,
						access: body.access,
					} );
					booked( body.message );

					return;
				}

				showRejection( body );

				// The slot went while the form was being filled in: booked
				// by someone else, or begun, or the day itself over. Ask
				// again, so the visitor picks from what is left without
				// reloading, and put them back on the slots.
				const errors = body?.data?.errors || {};

				if (
					state.selected &&
					( 'r2o_lab_booking_slot_taken' === body?.code ||
						errors.time_slot ||
						errors.slot_date )
				) {
					refreshMonth( monthOf( state.selected ) ).then(
						focusFirstInvalid
					);

					return;
				}

				focusFirstInvalid();
			} )
			.catch( () => {
				settle();
				showMessage( text( 'bookingFailed' ), 'error' );
			} );
	}

	function settle() {
		state.submitting = false;
		submit.removeAttribute( 'aria-disabled' );
		form.removeAttribute( 'aria-busy' );
	}

	/**
	 * Leaves only the confirmation, and moves focus to it: the button that
	 * had focus is gone with the rest of the form.
	 *
	 * @param {string} words The route's confirmation.
	 */
	function booked( words ) {
		container.dataset.r2oBooked = '';
		showMessage( words, 'success' );
		message.focus();
	}

	picker?.addEventListener( 'change', onModuleChange );
	form.addEventListener( 'submit', onSubmit );

	showMonth( state.view );
}

document.querySelectorAll( '[data-r2o-lab-booking]' ).forEach( init );
